import React from 'react';
import {
  Button,
  Divider,
  Form,
  MultiSelect,
  Popover,
  Stack,
  StyledContainer,
  TextField,
  useTranslation,
} from '@piwikpro/ui-components';
import { PermissionLevel, ResourcePermissionLevel } from '@piwikpro/access-control-crate';
import { FormValidator } from '@piwikpro/form-crate';
import { withInject } from '@piwikpro/platform';
import styles from './UserPermissionsList.module.css';
import { permissionOptions, PermissionOptionsKind } from '../../../../permissionOptions';

export interface PermissionFilters {
  userPermission: ResourcePermissionLevel[]
  groupPermission: ResourcePermissionLevel[]
  overallPermission: ResourcePermissionLevel[]
}

export const emptyPermissionFilters: PermissionFilters = {
  userPermission: [],
  groupPermission: [],
  overallPermission: [],
};

const OPTION_ORDER = ['manage', 'edit-publish', 'edit', 'view', 'no-access'] as const;

const countActiveFilters = (filters: PermissionFilters) => (
  filters.userPermission.length
  + filters.groupPermission.length
  + filters.overallPermission.length
);

const selectedLabel = (
  selected: ResourcePermissionLevel[],
  levels: PermissionLevel[],
  allLabel: string,
) => {
  if (!selected?.length) {
    return allLabel;
  }

  return OPTION_ORDER
    .filter(id => selected.includes(id))
    .map(id => levels.find(level => level.id === id)?.name || id)
    .join(', ');
};

interface Props {
  dataId: string
  kind: PermissionOptionsKind
  levels: PermissionLevel[]
  filters: PermissionFilters
  search: string
  searchPlaceholder: string
  onSearchChange: (event: { target: { value: string | number } }) => void
  onSearchClear: () => void
  onFilterApply: (filters: PermissionFilters) => void
}

interface InjectedProps {
  validation: FormValidator
}

const PermissionMultiSelect = ({
  label,
  name,
  dataId,
  value,
  options,
  allLabel,
  levels,
  onChange,
}: {
  label: string
  name: string
  dataId: string
  value: ResourcePermissionLevel[]
  options: ReturnType<typeof permissionOptions>
  allLabel: string
  levels: PermissionLevel[]
  onChange: (value: ResourcePermissionLevel[]) => void
}) => (
  <Stack.Item fill>
    <MultiSelect
      label={label}
      name={name}
      dataId={dataId}
      fullWidth
      wrap
      customMaxWidth={600}
      value={value}
      getSelectedName={(selected: ResourcePermissionLevel[]) => selectedLabel(selected, levels, allLabel)}
      onChange={(event: { target: { value: ResourcePermissionLevel[] } }) => onChange(event.target.value)}
      options={[{
        id: 'permissions',
        items: options as unknown as { id: string, value: ResourcePermissionLevel, name: string }[],
      }]}
    />
  </Stack.Item>
);

const UserPermissionsFilters = ({
  dataId,
  kind,
  levels,
  filters,
  search,
  searchPlaceholder,
  onSearchChange,
  onSearchClear,
  onFilterApply,
  validation,
}: Props & InjectedProps) => {
  const { t } = useTranslation('administration');
  const yup = validation.getYup();
  const validationSchema = validation.createValidationSchema(() => ({
    filters: yup.object().shape({
      userPermission: yup.array(),
      groupPermission: yup.array(),
      overallPermission: yup.array(),
    }),
  }));
  const options = permissionOptions(levels, t, kind);
  const allLabel = t('users.permissions.filters.all');
  const activeCount = countActiveFilters(filters);

  return (
    <Form
      id={`${dataId}-filters`}
      initialValues={{ filters }}
      validationSchema={validationSchema}
      onSubmit={(values: { filters: PermissionFilters }, actions) => {
        onFilterApply(values.filters);
        actions.setSubmitting(false);
      }}
      render={(form) => {
        const {
          dirty,
          values,
          setFieldValue,
          resetForm,
        } = form;

        return (

          <StyledContainer border={['left', 'right', 'top']} padding="narrow" backgroundColor="gray">
            <Stack spacing="narrow" verticalAlignment="center">
              <Stack.Item fill>
                <TextField
                  value={search}
                  name={`${dataId}-search`}
                  icon="search"
                  onChange={onSearchChange}
                  onClear={onSearchClear}
                  placeholder={searchPlaceholder}
                  clearable
                />
              </Stack.Item>
              <Stack.Item>
                <Popover
                  position="bottom-end"
                  speechBubble
                  padding="medium"
                  dataId={`${dataId}-filters`}
                  activatorElement={(
                    <Button
                      text={t('users.permissions.filters.button')}
                      clickableBadge={activeCount > 0 ? {
                        text: String(activeCount),
                        onClick: () => {
                          setFieldValue('filters', emptyPermissionFilters, false);
                          onFilterApply(emptyPermissionFilters);
                        },
                      } : undefined}
                    />
                  )}
                >
                  {close => (
                    <div className={styles.filtersPanel}>
                      <Stack vertical>
                        <PermissionMultiSelect
                          label={t('users.permissions.column.userPermission')}
                          name={`${dataId}-filter-user-permission`}
                          dataId={`${dataId}-filter-user-permission`}
                          value={values.filters.userPermission}
                          options={options}
                          allLabel={allLabel}
                          levels={levels}
                          onChange={userPermission => setFieldValue('filters', {
                            ...values.filters,
                            userPermission,
                          })}
                        />
                        <PermissionMultiSelect
                          label={t('users.permissions.column.highestGroupPermission')}
                          name={`${dataId}-filter-group-permission`}
                          dataId={`${dataId}-filter-group-permission`}
                          value={values.filters.groupPermission}
                          options={options}
                          allLabel={allLabel}
                          levels={levels}
                          onChange={groupPermission => setFieldValue('filters', {
                            ...values.filters,
                            groupPermission,
                          })}
                        />
                        <PermissionMultiSelect
                          label={t('users.permissions.column.overallPermission')}
                          name={`${dataId}-filter-overall-permission`}
                          dataId={`${dataId}-filter-overall-permission`}
                          value={values.filters.overallPermission}
                          options={options}
                          allLabel={allLabel}
                          levels={levels}
                          onChange={overallPermission => setFieldValue('filters', {
                            ...values.filters,
                            overallPermission,
                          })}
                        />
                        <Stack.Item fill>
                          <Divider noMargin />
                        </Stack.Item>
                        <Stack.Item fill>
                          <Stack verticalAlignment="center" spacing="narrow">
                            <Stack.Item fill>
                              <Button
                                appearance="simple"
                                text={t('users.permissions.filters.clear')}
                                disabled={countActiveFilters(values.filters) === 0 && !dirty}
                                onClick={() => setFieldValue('filters', emptyPermissionFilters)}
                              />
                            </Stack.Item>
                            <Stack.Item>
                              <Button
                                text={t('users.permissions.filters.cancel')}
                                onClick={() => {
                                  resetForm({
                                    filters: {
                                      userPermission: [...filters.userPermission],
                                      groupPermission: [...filters.groupPermission],
                                      overallPermission: [...filters.overallPermission],
                                    },
                                  });
                                  close();
                                }}
                              />
                            </Stack.Item>
                            <Stack.Item>
                              <Form.Submit
                                text={t('users.permissions.filters.apply')}
                                onClick={() => {
                                  onFilterApply(values.filters);
                                  close();
                                }}
                              />
                            </Stack.Item>
                          </Stack>
                        </Stack.Item>
                      </Stack>
                    </div>
                  )}
                </Popover>
              </Stack.Item>
            </Stack>
          </StyledContainer>
        );
      }}
    />
  );
};

export default withInject<InjectedProps>({
  validation: 'FormCrate.validation',
})(UserPermissionsFilters);
